import { useState } from 'react';
import { AsyncBoundary, Drawer, Notice } from '@kollez/design-system';
import {
  useIntegrityFlags, useResolveIntegrityFlag,
  type IntegrityFlagKind, type IntegrityFlagStatus, type IntegrityFlagSeverity,
  type IntegrityFlagRow,
} from '@/lib/queries';

/**
 * /tpo/integrity (Page 69/N) — Integrity flag review queue.
 *
 * IntegrityFlag rows are created by the assessment engine when it detects
 * cheating signals (answer-match with another attempt, face mismatch,
 * paste-into-code-editor, impossible speed, etc.). Tenant-scoped review
 * queue lives here — TPO/principal clears, explains (marks legitimate),
 * requires retest, or voids the attempt (voided flags open a 7-day appeal
 * window for the student).
 */

const KIND_LABEL: Record<IntegrityFlagKind, string> = {
  answerMatch: 'Answer match',
  faceMismatch: 'Face mismatch',
  impossibleSpeed: 'Impossible speed',
  tabSwitch: 'Tab switch',
  paste: 'Paste detected',
  codeSimilarity: 'Code similarity',
  networkTiming: 'Network timing',
};

const STATUS_LABEL: Record<IntegrityFlagStatus, string> = {
  open: 'Open',
  cleared: 'Cleared',
  explained: 'Explained',
  retest: 'Retest required',
  voided: 'Voided',
};

function statusTone(s: IntegrityFlagStatus): string {
  if (s === 'open') return 'badge-warning';
  if (s === 'cleared') return 'badge-success';
  if (s === 'explained') return 'badge-info';
  if (s === 'retest') return 'badge-info';
  if (s === 'voided') return 'badge-danger';
  return '';
}

function severityTone(s: IntegrityFlagSeverity): string {
  if (s === 'high') return 'badge-danger';
  if (s === 'medium') return 'badge-warning';
  return '';
}

function fmtDate(iso?: string): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return iso;
  }
}

type Resolution = 'cleared' | 'explained' | 'retest' | 'voided';

export function TpoIntegrityFlagsPage() {
  const [status, setStatus] = useState<IntegrityFlagStatus | ''>('open');
  const [severity, setSeverity] = useState<IntegrityFlagSeverity | ''>('');
  const [kind, setKind] = useState<IntegrityFlagKind | ''>('');
  const [open, setOpen] = useState<IntegrityFlagRow | null>(null);
  const [action, setAction] = useState<Resolution>('cleared');
  const [reason, setReason] = useState('');

  const list = useIntegrityFlags({ status, severity, kind, sortBy: 'createdAt', sortDir: 'desc', limit: 100 });
  const resolve = useResolveIntegrityFlag();

  const rows = list.data?.rows ?? [];
  const summary = list.data?.summary;

  const closeDrawer = () => {
    setOpen(null);
    setReason('');
    setAction('cleared');
  };

  const submit = () => {
    if (!open || reason.trim().length < 5) return;
    resolve.mutate(
      { id: open._id, status: action, reason: reason.trim() },
      { onSuccess: () => closeDrawer() },
    );
  };

  return (
    <div className="stack">
      <div className="page-header">
        <div className="ph-text">
          <p className="eyebrow">Assessments · Integrity</p>
          <h1 className="h-page">Integrity flags</h1>
          <p className="subhead">
            Every cheating signal the assessment engine caught. Review each one — clear,
            explain, require a retest, or void the attempt. Voided flags open a 7-day appeal
            window for the student.
          </p>
        </div>
      </div>

      {summary && (
        <div className="grid gap-3 grid-cols-2 lg:grid-cols-5">
          <div className="card kpi">
            <span className="kpi-label">Open</span>
            <span className="kpi-value">{summary.open}</span>
            <span className="kpi-meta">need review</span>
          </div>
          <div className="card kpi"><span className="kpi-label">Cleared</span><span className="kpi-value">{summary.cleared}</span></div>
          <div className="card kpi"><span className="kpi-label">Explained</span><span className="kpi-value">{summary.explained}</span></div>
          <div className="card kpi"><span className="kpi-label">Voided</span><span className="kpi-value">{summary.voided}</span></div>
          <div className="card kpi"><span className="kpi-label">In appeal</span><span className="kpi-value">{summary.appealing}</span></div>
        </div>
      )}

      <div className="card">
        <div className="filterbar">
          <select className="select" value={status} onChange={(e) => setStatus(e.target.value as IntegrityFlagStatus | '')}>
            <option value="">Any status</option>
            {(Object.entries(STATUS_LABEL) as Array<[IntegrityFlagStatus, string]>).map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
          <select className="select" value={severity} onChange={(e) => setSeverity(e.target.value as IntegrityFlagSeverity | '')}>
            <option value="">Any severity</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
          <select className="select" value={kind} onChange={(e) => setKind(e.target.value as IntegrityFlagKind | '')}>
            <option value="">Any kind</option>
            {(Object.entries(KIND_LABEL) as Array<[IntegrityFlagKind, string]>).map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
          <span className="fb-count">{list.data?.meta?.total ?? rows.length}</span>
        </div>

        <AsyncBoundary
          kind="table"
          inCard
          loading={list.isLoading}
          error={list.error}
          empty={!list.isLoading && rows.length === 0}
          emptyTitle="No flags in scope"
          emptyHint="Nothing to review right now. Widen the filters to see resolved history."
          onRetry={() => void list.refetch()}
        >
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Detected</th>
                  <th>Kind</th>
                  <th>Severity</th>
                  <th>Assessment</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r._id}>
                    <td className="cell-sub">{fmtDate(r.createdAt)}</td>
                    <td className="cell-title">{KIND_LABEL[r.kind]}</td>
                    <td><span className={`badge ${severityTone(r.severity)}`}>{r.severity}</span></td>
                    <td className="cell-sub">
                      {typeof r.assessment === 'object' && r.assessment?.name ? r.assessment.name : '—'}
                    </td>
                    <td><span className={`badge ${statusTone(r.status)}`}>{STATUS_LABEL[r.status]}</span></td>
                    <td>
                      {r.status === 'open' ? (
                        <button className="btn btn-sm" onClick={() => setOpen(r)}>Review</button>
                      ) : (
                        <button className="btn btn-sm btn-ghost" onClick={() => setOpen(r)}>View</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </AsyncBoundary>
      </div>

      <Notice tone="warning">
        <b>Voiding is not a punishment.</b> Void only when the evidence is strong. Voided flags
        auto-open a 7-day appeal window; the student can submit a written explanation, and
        another reviewer must decide.
      </Notice>

      <Drawer open={Boolean(open)} onClose={closeDrawer} title="Integrity flag" size="lg">
        {open && (
          <div className="stack">
            <div>
              <div className="flex items-baseline gap-2 flex-wrap">
                <h2 className="h-section m-0">{KIND_LABEL[open.kind]}</h2>
                <span className={`badge ${severityTone(open.severity)}`}>{open.severity}</span>
                <span className={`badge ${statusTone(open.status)}`}>{STATUS_LABEL[open.status]}</span>
              </div>
              <p className="cell-sub m-0 mt-1">
                Detected {fmtDate(open.createdAt)}
                {typeof open.assessment === 'object' && open.assessment?.name && ` · ${open.assessment.name}`}
              </p>
            </div>

            {open.evidence && (
              <div className="card card-pad">
                <div className="eyebrow">Evidence</div>
                <pre className="text-xs whitespace-pre-wrap break-words m-0 mt-1">
                  {JSON.stringify(open.evidence, null, 2)}
                </pre>
              </div>
            )}

            {open.reason && (
              <div className="card card-pad">
                <div className="eyebrow">Reviewer note</div>
                <p className="m-0 mt-1 whitespace-pre-line">{open.reason}</p>
              </div>
            )}

            {open.appealText && (
              <div className="card card-pad">
                <div className="eyebrow">Student appeal</div>
                <p className="m-0 mt-1 whitespace-pre-line">{open.appealText}</p>
                {open.appealSubmittedAt && (
                  <p className="cell-sub m-0 mt-1">Submitted {fmtDate(open.appealSubmittedAt)}</p>
                )}
              </div>
            )}

            {open.status === 'open' && (
              <div className="card card-pad">
                <div className="eyebrow mb-2">Resolve</div>
                <div className="grid gap-2 grid-cols-2">
                  {(['cleared', 'explained', 'retest', 'voided'] as Resolution[]).map((r) => (
                    <label key={r} className={`card card-pad cursor-pointer ${action === r ? 'border-primary' : ''}`}>
                      <input type="radio" name="resolution" value={r} checked={action === r} onChange={() => setAction(r)} className="mr-2" />
                      <b>{r === 'cleared' ? 'Clear' : r === 'explained' ? 'Explain (legit)' : r === 'retest' ? 'Retest' : 'Void'}</b>
                      <div className="cell-sub">
                        {r === 'cleared' && 'False positive; no action.'}
                        {r === 'explained' && 'Signal was real but the student had a valid reason.'}
                        {r === 'retest' && 'Give the student another attempt on a fresh form.'}
                        {r === 'voided' && 'Discard the attempt. Opens 7-day appeal window.'}
                      </div>
                    </label>
                  ))}
                </div>
                <label className="block mt-3">
                  <span className="eyebrow">Reason (5-500 chars)</span>
                  <textarea
                    className="input mt-1"
                    rows={4}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="What led you to this decision? Cite the specific evidence."
                  />
                </label>
                <div className="flex justify-end gap-2 mt-3">
                  <button className="btn btn-ghost" onClick={closeDrawer}>Cancel</button>
                  <button
                    className="btn btn-primary"
                    disabled={reason.trim().length < 5 || resolve.isPending}
                    onClick={submit}
                  >
                    {resolve.isPending ? 'Saving…' : 'Save decision'}
                  </button>
                </div>
              </div>
            )}

            {open.appealDeadline && open.status === 'voided' && (
              <Notice tone="info">
                Appeal window closes {fmtDate(open.appealDeadline)}. Until then, the student may
                submit a written appeal from their Tests page.
              </Notice>
            )}
          </div>
        )}
      </Drawer>
    </div>
  );
}
